import * as styles from "@components/cart/sticky-add-to-cart.module.css"
import CrateSizeModal from "@components/crate-size-modal"
import ClassicHarnessSizePopup from "@components/crate-size-modal/classic-harness-size-popup.js"
import Link from "@components/link"
import RichText from "@components/rich-text"
import AlertDialog from "@src/components/alert"
import ProductHeader from "@src/components/product/header"
import useToggleStates from "@src/hooks/useToggleStates"
import { trackAddToCart, trackEvent } from "@src/services/analytics"
import { useSiteSettingsStore } from "@src/hooks/useStore"
import copyIcon from "@svgs/copy-icon.svg"
import {
  getLineItemsData,
  getProductOriginList,
  setQueryStringParameter,
  trackProductView,
  trackTripleWhaleAddToCart,
  variantIdFromShopifyId,
} from "@utils/product"
import getValue from "get-value"
import { isEqual } from "lodash"
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import OptionSelector from "../option-selector"
import { useCartStore, useQuickViewStore, useProductStore } from "@src/hooks/useStore"
import AddToCart from "@src/components/add-to-cart"
import ShippingConditions from "../shipping-conditions"
import InlineCrossSell from "@src/components/inline-cross-sell"

// const getOptionTitle = variant => {
//   const { selectedOptions } = variant
//   const sizeMap = {
//     XS: "Extra Small",
//     S: "Small",
//     M: "Medium",
//     I: "Intermediate",
//     L: "Large",
//   }
//   const selectedOption = selectedOptions?.find(option => option.name === "Size")
//   const optionValue = getValue(selectedOption, "value")

//   return getValue(sizeMap, `${optionValue}`)
// }

const ProductDetails = ({
  productEntry,
  isBowl,
  isClassicHarness,
  productOptions,
  crateSizes,
  quizLink,
  fromList,
  sanityDiscountCodes,
}) => {
  const { setAlert } = useSiteSettingsStore(["setAlert"]);
  const { 
    currentVariant, 
    setCurrentVariant, 
    addedCrossSellProducts, 
    removeAddedCrossSellProducts, 
    price,
    priceRaw,
    compareAtPrice, 
    accumulatedPrice,
    quantity,
    discountDisplay,
    setCouponDiscountDisplay
  } = useProductStore(["currentVariant", "setCurrentVariant", "addedCrossSellProducts", "removeAddedCrossSellProducts", "price", "priceRaw", "compareAtPrice", "accumulatedPrice", "quantity", "discountDisplay", "setCouponDiscountDisplay"]);
  
  const {
    setQuickViewProductList,
  } = useQuickViewStore(["setQuickViewProductList"])

  const { addLineItems, cart, loading } = useCartStore(["addLineItems", "cart", "loading"]);

  const { toggleAllFunction } = useToggleStates();

  useEffect(() => {
    if (cart.discountCodes) setCouponDiscountDisplay(productEntry._id, sanityDiscountCodes)
  }, [cart.discountCodes?.[0]?.code])

  const { atcLabel } = useMemo(() => {
    if (currentVariant._id) {
      return currentVariant;
    }

    return {};
  }, [currentVariant])

  const {
    variants,
    title,
    summary,
  } = productEntry

  const extraDiscountMessage =
    currentVariant?.extraDiscountMessage || productEntry?.extraDiscountMessage
  const hasSizeGuide = crateSizes?.length > 0

  const available = useMemo(() => currentVariant?.availableForSale, [currentVariant])
  const isAvailableWithPrime = useMemo(() => currentVariant?.isAvailableWithPrime, [currentVariant])
  const isWatchlist = useMemo(() => currentVariant?.watchlist, [currentVariant])

  useEffect(() => {
    setQuickViewProductList(fromList)
  }, [fromList])

  useEffect(() => {
    if ("URLSearchParams" in window) {
      const urlVariantId = new URLSearchParams(location.search).get("variantId")

      const alreadySet =
        variantIdFromShopifyId(getValue(currentVariant, "shopifyId")) ===
        urlVariantId

      if (urlVariantId && !alreadySet) {
        setTimeout(() => {
          const targetVariant = variants.find(
            ({ shopifyId }) => shopifyId == urlVariantId
          )

          if (targetVariant) {
            setCurrentVariant({ ...targetVariant }, productEntry, sanityDiscountCodes)
          }
        }, 250)
      }
    }
  }, [])

  const handleOptionChange = (optionIndex, { target }) => {
    const { value } = target
    const currentOptions = [...currentVariant.selectedOptions]

    if (currentOptions[optionIndex].value !== value) {
      currentOptions[optionIndex] = {
        ...currentOptions[optionIndex],
        value,
      }

      const selectedVariant = productEntry.variants.find(
        ({ selectedOptions }) => isEqual(currentOptions, selectedOptions)
      )
      const { shopifyId: selectedVariantId } = selectedVariant

      setCurrentVariant({ ...selectedVariant }, productEntry, sanityDiscountCodes)
      setQueryStringParameter("variantId", selectedVariantId)
    }
  }

  const allPrices = { price, compareAtPrice, discountDisplay }

  const appliedDiscount = sanityDiscountCodes?.find(
    discount =>
      cart?.discountCodes?.find(
        cartDiscount =>
          discount?.code?.toLowerCase() === cartDiscount?.code?.toLowerCase()
      ) && discount?.products?.find(prod => prod._id === productEntry._id)
  )

  const discountAmountFromCode =
    appliedDiscount?.discountType === "fixed"
      ? appliedDiscount?.fixedAmount
      : priceRaw *
        (appliedDiscount?.percentageAmount / 100)

  const watchlistFormTriggerClassName = useMemo(() => {
    const { shopifyIdBase64Encoded } = currentVariant

    const selectedProductVariant = variants.find(
      match => match.shopifyIdBase64Encoded === shopifyIdBase64Encoded
    )

    if (!selectedProductVariant) {
      return false
    }

    return selectedProductVariant.status?.watchlist === true
      ? selectedProductVariant.status?.watchlistFormTriggerClassName ||
      "ju-trigger-watchlist"
      : null
  }, [currentVariant, variants])

  const watchlistDescription = useMemo(() => {
    const { shopifyIdBase64Encoded } = currentVariant

    const selectedProductVariant = variants.find(
      match => match.shopifyIdBase64Encoded === shopifyIdBase64Encoded
    )

    if (!selectedProductVariant) {
      return false
    }

    return selectedProductVariant.status?.watchlist === true
      ? selectedProductVariant.status?.watchlistDescription
      : null
  }, [currentVariant, variants])

  const [copied, setCopied] = useState(false)

  const extraDiscountRegex = /:\s*(.*)$/

  const detectScriptPart = extraDiscountMessage
    ?.replace(/[*_]/g, "")
    ?.match(extraDiscountRegex)

  const extraDiscountCode = detectScriptPart && detectScriptPart[1]

  const extraDiscountText =
    extraDiscountCode && extraDiscountMessage?.replace(extraDiscountCode, "")

  const handleCopyClick = e => {
    e.preventDefault()
    navigator.clipboard.writeText(extraDiscountCode)
    setCopied(true)

    setTimeout(() => {
      setCopied(false)
    }, 2000)
  }

  const handleAddToCart = async () => {
    const { 
      lineItems, 
      lineItemsData
    } = getLineItemsData(productEntry, currentVariant, quantity, addedCrossSellProducts, fromList)

    await addLineItems(lineItems)
    toggleAllFunction('drawer', true)
    removeAddedCrossSellProducts()
    trackAddToCart(lineItemsData, fromList || getProductOriginList(productEntry))
    trackTripleWhaleAddToCart(currentVariant.shopifyId);
  }

  useEffect(() => {
    if (currentVariant._id) {
      trackProductView(productEntry, currentVariant, fromList)
    }
  }, [currentVariant])

  const [isSizeDetailsOpen, setIsSizeDetailsOpen] = useState(false)
  const closeSizeDetails = () => {
    setIsSizeDetailsOpen(false)
  }
  const openSizeDetails = useCallback(() => {
    setIsSizeDetailsOpen(true)
  }, [setIsSizeDetailsOpen])

  const extraDiscount = () =>
    extraDiscountMessage && (
      <button
        onClick={handleCopyClick}
        className="relative inline-block gap-1 items-center w-full max-md:flex max-md:justify-center md:items-start text-left bg-steadfast-red bg-opacity-20 rounded-lg cursor-pointer px-3 pt-1.5 pb-1.5 text-soft-black font-light text-sm"
      >
        <div className="flex items-center gap-1 shrink-0">
          <span className="inline">
            {extraDiscountText}
            <span className="font-medium">{extraDiscountCode}</span>
          </span>
          <img className="h-4 ml-2" src={copyIcon} alt="Copy" />
        </div>
        <div
          style={{
            transition: "opacity 350ms ease-out",
            opacity: copied ? "1" : "0",
            pointerEvents: copied ? "auto" : "none",
          }}
          className="arrow-box-down flex justify-end bg-black/70 rounded-sm absolute top-[-70%] right-0 transition-transform"
        >
          <p className="text-sm text-[#ffffff] p-1">Copied to clipboard</p>
        </div>
      </button>
    )

  const sizeGuide = () =>
    (hasSizeGuide || isClassicHarness) &&
    quizLink && (
      <div className="is-sticky-hidden">
        <Link
          to={quizLink}
          className="font-medium underline"
          target="_blank"
          rel="noreferrer"
        >
          Take the Sizing Quiz
        </Link>
      </div>
    )

  useEffect(() => {
    if (!isAvailableWithPrime && !available) {
      setAlert(<AlertDialog role="alert">This product is out of stock</AlertDialog>)
    } else {
      setAlert(<></>)
    }
  }, [currentVariant, isAvailableWithPrime, available])

  const trackUpsellClicks = () => {
    trackEvent("upsell_click", {});
  }

  return (
    <div
      className={`is-sticky-mb-0 pt-4 md:pt-0 flex flex-col ${styles.stickyAtc}`}
    >
      <ProductHeader
        title={title}
        summary={summary}
        productIds={[productEntry.yotpoProductId]}
        rating={productEntry?.rating}
        reviewCount={productEntry?.reviewCount}
      />
      <div className="flex flex-col pt-5 pb-5 gap-2.5 md:gap-5">
        <OptionSelector
          {...{
            currentVariant,
            productOptions,
            variants,
            hideInStickyATC: isBowl || false,
            handleOptionChange,
            prices: allPrices,
          }}
        />
        {isClassicHarness && (
          <div className="flex justify-end">
            <Link
              onClick={openSizeDetails}
              className="hover:text-safety-green focus:outline-none transition ease-in-out duration-150 antialiased font-medium underline"
            >
              View Size Guide
            </Link>
          </div>
        )}

        {(extraDiscountMessage || hasSizeGuide || isClassicHarness) && (
          <div className="gap-5 flex justify-between items-center">
            <div className="max-md:hidden max-w-[60%]">{extraDiscount()}</div>
            {sizeGuide()}
          </div>
        )}

        {watchlistDescription && (
          <RichText text={watchlistDescription} className={"mb-6"} />
        )}

        {isWatchlist && (
          <div className="flex rounded-md is-sticky-mt-2">
            <button
              type="button"
              className={`btn btn-secondary hover:btn-primary w-full is-sticky-sm ${watchlistFormTriggerClassName}`}
            >
              {loading ? "Adding" : atcLabel}
              <span className="is-sticky-price">{accumulatedPrice}</span>
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2.5 md:gap-5 PDP-elements-order-control">
        <ShippingConditions {...{ productEntry }} />
        {/* <StyluxCta
          variant={currentVariant}
          className="-mb-2.5 md:-mb-5"
          addToCartSelector='[data-add-to-cart-btn="true"], [data-add-to-cart-btn="true"] *'
          takeOverAtc={Boolean(productEntry?.title?.includes("Crate Bowl"))}
          handleAddItemsToCart={handleAddToCart}
        /> */}
        {productEntry?.crossSell && (
          <InlineCrossSell
            {...{ products: productEntry?.crossSell, trackUpsellClicks }}
          />
        )}
      </div>

      <AddToCart 
        product={productEntry} 
        handleAddToCart={handleAddToCart} 
        discountAmountFromCode={discountAmountFromCode}
      />

      {extraDiscountMessage && (
        <div className="min-md:hidden md:mt-2.5">{extraDiscount()}</div>
      )}
      {hasSizeGuide && (
        <CrateSizeModal
          isOpen={isSizeDetailsOpen}
          toggle={closeSizeDetails}
          items={crateSizes}
          quizLink={quizLink}
        />
      )}
      {isClassicHarness && (
        <ClassicHarnessSizePopup
          isOpen={isSizeDetailsOpen}
          toggle={closeSizeDetails}
        />
      )}
    </div>
  )
}

export default ProductDetails
